'use client';;!function(){try{var e="undefined"!=typeof window?window:"undefined"!=typeof global?global:"undefined"!=typeof globalThis?globalThis:"undefined"!=typeof self?self:{};e._sentryModuleMetadata=e._sentryModuleMetadata||{},e._sentryModuleMetadata[(new e.Error).stack]=Object.assign({},e._sentryModuleMetadata[(new e.Error).stack],{"_sentryBundlerPluginAppKey:tillamook-com":true});}catch(e){}}();

import { useMemo } from 'react';

import cx from 'classnames';
import PropTypes from 'prop-types';

import ButtonCTA from 'components/ui/ButtonCTA';
import Icon from 'components/ui/Icon';
import RichText from 'components/ui/RichText';
import SvgIcon from 'components/ui/SvgIcon';
import Text from 'components/ui/Text';

import { themeMapping } from './themeMapping';
import {
    checkForLongWords,
    convertHardBreaksToLineBreaks,
    getCopyRichTextOverrides,
    getHeadlineRichTextOverrides,
    isRichText,
    isValidContent,
} from './utils';

import styles from './Lockup.module.scss';

const Lockup = ({
    icon,
    eyebrow,
    headline,
    copy,
    ctas,
    ctaSlot,
    theme = 'Large',
    wordLengthThreshold = 9, // Default threshold for long words (i.e. EXTRAORDINARY)
}) => {
    const themeConfig = themeMapping[theme] || themeMapping['Large'];

    // handle content from TextElementProps
    const eyebrowContent = eyebrow?.text;
    const eyebrowElement = eyebrow?.as || 'p';
    const eyebrowThemeOverrides = eyebrow?.themeOverrides;

    const headlineContent = headline?.text;
    const headlineElement = headline?.as || 'h2';
    const headlineThemeOverrides = headline?.themeOverrides;

    const copyContent = copy?.text;
    const copyElement = copy?.as || 'p';
    const copyThemeOverrides = copy?.themeOverrides;

    // check if content is valid and rich text
    const validHeadlineContent =
        headlineContent && isValidContent(headlineContent);
    const validCopyContent = copyContent && isValidContent(copyContent);

    const headlineIsRichText = validHeadlineContent
        ? isRichText(headlineContent)
        : false;
    const copyIsRichText = validCopyContent ? isRichText(copyContent) : false;

    // Check for long words in headline
    const hasLongWord = useMemo(() => {
        return checkForLongWords(
            validHeadlineContent ? headlineContent : null,
            headlineIsRichText,
            wordLengthThreshold
        );
    }, [
        validHeadlineContent,
        headlineContent,
        headlineIsRichText,
        wordLengthThreshold,
    ]);

    // get rich text overrides
    const headlineRichTextOverrides = getHeadlineRichTextOverrides(
        theme,
        headlineElement,
        headlineThemeOverrides
    );

    // Convert hard breaks (multiple paragraphs) to line breaks for headlines
    const processedHeadlineContent = useMemo(() => {
        if (headlineIsRichText && validHeadlineContent) {
            return convertHardBreaksToLineBreaks(headlineContent);
        }
        return headlineContent;
    }, [headlineIsRichText, validHeadlineContent, headlineContent]);

    const copyRichTextOverrides = getCopyRichTextOverrides(
        theme,
        copyElement,
        copyThemeOverrides
    );

    const validCTAs = useMemo(() => {
        if (!ctas) return [];
        const ctaArray = Array.isArray(ctas) ? ctas : [ctas];
        return ctaArray.filter(
            cta => cta && cta.text && (cta.link || cta.assetDownloadLink)
        );
    }, [ctas]);

    return (
        <div
            className={cx(styles.root, styles[theme], {
                [styles.hasLongWord]: hasLongWord,
            })}
        >
            {typeof icon === 'string' && icon !== 'none' ? (
                <SvgIcon className={styles.icon} type={icon} />
            ) : (
                icon?.icon?.url && (
                    <div className={styles.icon}>
                        <Icon src={icon.icon.url} />
                    </div>
                )
            )}
            {eyebrowContent && typeof eyebrowContent === 'string' && (
                <Text
                    as={eyebrowElement}
                    className={styles.eyebrow}
                    baseTheme={eyebrowThemeOverrides?.baseTheme || 'labelSmall'}
                    themes={
                        eyebrowThemeOverrides?.themes || {
                            large: 'labelLarge',
                        }
                    }
                >
                    {eyebrowContent}
                </Text>
            )}
            {validHeadlineContent && !headlineIsRichText && (
                <Text
                    as={headlineElement}
                    className={styles.headline}
                    baseTheme={
                        headlineThemeOverrides?.baseTheme ||
                        themeConfig.headline.baseTheme
                    }
                    themes={
                        headlineThemeOverrides?.themes ||
                        themeConfig.headline.themes
                    }
                >
                    {headlineContent}
                </Text>
            )}
            {validHeadlineContent && headlineIsRichText && (
                <RichText
                    richText={processedHeadlineContent}
                    overrides={headlineRichTextOverrides}
                />
            )}
            {validCopyContent && !copyIsRichText && (
                <div className={styles.copy}>
                    <Text
                        as={copyElement}
                        className={styles.paragraph}
                        baseTheme={
                            copyThemeOverrides?.baseTheme ||
                            themeConfig.copy.baseTheme
                        }
                        themes={
                            copyThemeOverrides?.themes ||
                            themeConfig.copy.themes
                        }
                    >
                        {copyContent}
                    </Text>
                </div>
            )}
            {validCopyContent && copyIsRichText && (
                <div className={styles.copy}>
                    <RichText
                        richText={copyContent}
                        overrides={copyRichTextOverrides}
                    />
                </div>
            )}
            {ctaSlot ? (
                <div className={styles.ctas}>{ctaSlot}</div>
            ) : validCTAs.length > 0 ? (
                <div className={styles.ctas}>
                    {validCTAs.map((cta, index) => (
                        <ButtonCTA key={index} {...cta} />
                    ))}
                </div>
            ) : null}
        </div>
    );
};

Lockup.propTypes = {
    icon: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.shape({
            icon: PropTypes.shape({
                url: PropTypes.string,
            }),
        }),
    ]),
    eyebrow: PropTypes.shape({
        text: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
        as: PropTypes.string,
        themeOverrides: PropTypes.shape({
            baseTheme: PropTypes.string,
            themes: PropTypes.object,
        }),
    }),
    headline: PropTypes.shape({
        text: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
        as: PropTypes.string,
        themeOverrides: PropTypes.shape({
            baseTheme: PropTypes.string,
            themes: PropTypes.object,
        }),
    }),
    copy: PropTypes.shape({
        text: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
        as: PropTypes.string,
        themeOverrides: PropTypes.shape({
            baseTheme: PropTypes.string,
            themes: PropTypes.object,
        }),
    }),
    ctas: PropTypes.oneOfType([
        PropTypes.shape({
            text: PropTypes.string,
            link: PropTypes.object,
            assetDownloadLink: PropTypes.object,
            style: PropTypes.string,
            iconType: PropTypes.string,
            className: PropTypes.string,
        }),
        PropTypes.arrayOf(
            PropTypes.shape({
                text: PropTypes.string,
                link: PropTypes.object,
                assetDownloadLink: PropTypes.object,
                style: PropTypes.string,
                iconType: PropTypes.string,
                className: PropTypes.string,
            })
        ),
    ]),
    ctaSlot: PropTypes.node,
    theme: PropTypes.oneOf(['Small', 'Medium', 'Large', 'X-Large', 'XX-Large']),
    wordLengthThreshold: PropTypes.number,
};

export default Lockup;
